import { useEffect, useState } from "react";
import { useLocalization } from "../../context/LocalizationContext";
import { getCurrencySymbol } from "../../utils/currencySymbols";
import { useNavigate, useLocation } from "react-router-dom";
import ServiceOrderStatus from "../../shared/Enums/ServiceOrderStatus";
import RepairOrderStatuses from "../RepairOrder/RepairOrderStatuses";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import LoadingOverlay from "../common/LoadingOverlay";
import { showErrorMessage } from "../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../shared/Services/Loader/loaderService";
import { useVehicleContext } from "../../context/VehicleContext";
import InfoCards from "../ServiceDeskUser/common/InfoCards";
import PriceSummary from "../ServiceDeskUser/common/PriceSummary";
import useServiceFetching from "../../hooks/useServiceFetching";
import useServiceTotals from "../../hooks/useServiceTotals";
import ServiceTableRow from "./common/ServiceTableRow";
import ServiceListDisplay from "./common/ServiceListDisplay";

const SignedPage = () => {
  const ctx = useVehicleContext();
  const {
    status,
    setStatus,
    selectedServices,
    setSelectedServices,
    repairOrder,
    setRepairOrder,
    vehicle: propVehicle,
    customerId,
  } = ctx;
  const [originalSelectedServices, setOriginalSelectedServices] = useState([]);
  const [selectedDealershipId] = useSelectedDealership();
  const { t } = useLocalization();
  const location = useLocation();
  const navDealershipId = location?.state?.dealershipId;
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(
    propVehicle ?? location?.state?.vehicle ?? null,
  );
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("oem");
  const navigate = useNavigate();
  const navCustomerId = location?.state?.customerId;
  const navVehicleId = location?.state?.vehicleId;
  const id = navCustomerId || customerId || null;
  const vehicleId = navVehicleId || (propVehicle && propVehicle.id) || null;
  const [currencyCode, setCurrencyCode] = useState("USD");
  const dealershipId = selectedDealershipId;
  const [showInProgressConfirm, setShowInProgressConfirm] = useState(false);

  // Use custom hooks for service management
  const {
    services,
    motorServices,
    dealerServices,
    isLoading: servicesLoading,
  } = useServiceFetching(vehicleId, dealershipId, t);

  const { calculateServiceTypeTotal } = useServiceTotals(selectedServices);

  useEffect(() => {
    if (propVehicle && Object.keys(propVehicle).length > 0) {
      if (!vehicle || propVehicle.id !== vehicle.id) {
        setVehicle(propVehicle);
      }
    }
  }, [propVehicle]);

  useEffect(() => {
    if (
      status === ServiceOrderStatus.IN_PROGRESS &&
      originalSelectedServices.length === 0 &&
      selectedServices.length > 0
    ) {
      setOriginalSelectedServices(selectedServices.map((s) => ({ ...s })));
    }
    if (
      (status === ServiceOrderStatus.SIGNED ||
        status === ServiceOrderStatus.NEW) &&
      originalSelectedServices.length > 0
    ) {
      setOriginalSelectedServices([]);
    }
  }, [status, selectedServices, originalSelectedServices.length]);

  useEffect(() => {
    const loadRepairOrderItems = async () => {
      const idToFetch = repairOrder?.[0]?.repairOrderId;
      if (!idToFetch) return;
      try {
        showLoader();
        const payload = { repairOrderId: idToFetch };
        const repairOrderData = await apiManager.post(
          API_ENDPOINTS.REPAIR_ORDER_ITEM,
          payload,
        );
        if (repairOrderData && repairOrderData.data.length > 0) {
          const backendServices = repairOrderData.data.map((item) => ({
            id: item.serviceId || `service-${Date.now()}`,
            serviceId: item.serviceId,
            serviceName: item.serviceName,
            description: item.serviceDescription,
            type: item.serviceCategory,
            price: item.price || 0,
            status: item.status || 1,
            ...item,
          }));
          setSelectedServices(backendServices);
          setOriginalSelectedServices(backendServices.map((s) => ({ ...s })));
        } else {
          setSelectedServices([]);
          setOriginalSelectedServices([]);
        }
      } catch (err) {
        showErrorMessage("Failed to fetch repair order items");
        setSelectedServices([]);
        setOriginalSelectedServices([]);
      } finally {
        hideLoader();
      }
    };

    loadRepairOrderItems();
  }, []);

  const isServiceSelected = (service) => {
    return selectedServices.some(
      (s) =>
        (s.id && service.id && s.id === service.id) ||
        (s.serviceId && service.serviceId && s.serviceId === service.serviceId),
    );
  };

  const handleMoveToInProgress = async () => {
    setShowInProgressConfirm(true);
  };

  const confirmMoveToInProgress = async () => {
    setShowInProgressConfirm(false);
    setOriginalSelectedServices(selectedServices.map((s) => ({ ...s })));
    const repairOrderId = repairOrder[0]?.repairOrderId;
    const payload = {
      repairOrderId: repairOrder.data || repairOrderId,
      statusCode: ServiceOrderStatus.IN_PROGRESS,
    };
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.DEALER_ORDER_STATUS,
        payload,
      );
      if (response.status == 200) {
        if (location.state.fromRedirect) {
          navigate(location.pathname, {
            state: {
              ...location.state,
              status: ServiceOrderStatus.IN_PROGRESS,
              goToStep5: true,
              goToStep4: false,
            },
            replace: true,
          });
        }
        setStatus(ServiceOrderStatus.IN_PROGRESS);
      } else {
        showErrorMessage(t("failed_to_update_order_status"));
      }
    } catch (err) {
      showErrorMessage(err);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const fetchDealership = async () => {
      try {
        const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_ID, {
          id: dealershipId,
        });
        if (response.status == 200) {
          const data = response.data;
          setDealership(data);
          setCurrencyCode(data.currencyCode || "USD");
        } else {
          showErrorMessage(t("error_fetch_dealership"));
          setDealership(null);
          setCurrencyCode("USD");
        }
      } catch (err) {
        showErrorMessage(err);
      }
    };
    fetchDealership();

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiManager.post(
          API_ENDPOINTS.CUSTOMER_GET_BY_ID,
          { id },
        );
        if (response.status == 200) {
          const data = response.data;
          setCustomer(data);
          const vid = vehicle?.id || vehicleId;
          const newresponse = await apiManager.post(
            API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
            { id: vid },
          );
          if (newresponse.status == 200) {
            const data = newresponse.data;
            setVehicle(data);
          } else {
            showErrorMessage(t("error_fetch_vehicle"));
          }
        } else {
          showErrorMessage(t("error_fetch_customer"));
        }
      } catch (err) {
        showErrorMessage(err);
      }
      setLoading(false);
    };

    fetchData();
  }, [id, t, dealershipId]);

  const isLoading = loading || servicesLoading;
  const serviceError = error || services[activeTab]?.error;
  // Exclude declined services (status === 3) from totals
  const activeSelectedServices = selectedServices;
  const nonDeclinedServices = activeSelectedServices.filter(
    (service) => service.status !== 3,
  );
  const subtotalAmount = nonDeclinedServices.reduce(
    (sum, service) => sum + (parseFloat(service.price) || 0),
    0,
  );
  const taxRate = Number(dealership?.tax) || 0;
  const taxAmount = subtotalAmount * (taxRate / 100);
  const totalWithTax = subtotalAmount + taxAmount;

  const statusDisplayMap = {
    [ServiceOrderStatus.NEW]: RepairOrderStatuses.NEW,
    [ServiceOrderStatus.SIGNED]: RepairOrderStatuses.SIGNED,
    [ServiceOrderStatus.IN_PROGRESS]: RepairOrderStatuses.IN_PROGRESS,
    [ServiceOrderStatus.COMPLETED]: RepairOrderStatuses.COMPLETED,
    [ServiceOrderStatus.PENDING_APPROVAL]: RepairOrderStatuses.PENDING_APPROVAL,
    [ServiceOrderStatus.APPROVAL_EXPIRED]: RepairOrderStatuses.APPROVAL_EXPIRED,
  };

  const getStatusDisplay = (statusCode) => {
    const normalizedStatus = statusCode;
    return statusDisplayMap[normalizedStatus] || statusCode;
  };

  if (serviceError)
    return <div className="alert alert-danger">{serviceError}</div>;

  return (
    <div>
      {isLoading && <LoadingOverlay />}
      {(status === ServiceOrderStatus.SIGNED || status === "signed") && (
        <div className="container-fluid p-0 mb-2">
          <div className="row">
            <div className="col-md-6">
              <div style={{ fontWeight: "bold", fontSize: 18 }}>
                {t("status")}:{" "}
                <span style={{ color: "#ff9800" }}>
                  {getStatusDisplay(status)}
                </span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-end">
                <div
                  className="gap-2"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <button
                    className="btn btn-success"
                    onClick={handleMoveToInProgress}
                  >
                    {t("move_in_progress")}
                  </button>
                  <button
                    className="btn btn-blue btn-sm"
                    onClick={() => navigate("/second-step")}
                  >
                    {t("back")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showInProgressConfirm && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                {t("confirm_inprogress_service")}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={confirmMoveToInProgress}
                >
                  {t("yes")}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setShowInProgressConfirm(false)}
                >
                  {t("no")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="select-services-content">
        <div className="container-fluid p-0">
          <InfoCards t={t} vehicle={vehicle} customer={customer} />
          <div className="col-md-12">
            <div className="card mb-4">
              <PriceSummary
                t={t}
                currencyCode={currencyCode}
                calculateServiceTypeTotal={calculateServiceTypeTotal}
              />
              <div className="card-body">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-6 tab-content-card">
                      <ServiceListDisplay
                        services={services}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        isServiceSelected={isServiceSelected}
                        onServiceSelect={() => {}}
                        currencyCode={currencyCode}
                        selectDisabled={true}
                        t={t}
                      />
                    </div>
                    <div className="col-lg-6 selected-card">
                      <h2>{t("selected")}</h2>
                      <table className="table">
                        <tbody>
                          {activeSelectedServices.length > 0 ? (
                            activeSelectedServices.map((service, index) => (
                              <ServiceTableRow
                                key={`selected-${service.id || index}`}
                                service={service}
                                index={index}
                                currencyCode={currencyCode}
                                onRemove={() => {}}
                                canRemove={false}
                                canEdit={false}
                                isDeclined={service.status === 3}
                                t={t}
                              />
                            ))
                          ) : (
                            <tr className="no-services-text">
                              <td colSpan="4" className="text-center py-3">
                                {t("no_services_selected")}
                              </td>
                            </tr>
                          )}
                          {activeSelectedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{ borderTop: "2px solid #dee2e6" }}
                            >
                              <td></td>
                              <td className="text-start text-muted">
                                {t("tax")} ({taxRate}%)
                              </td>
                              <td className="mulish-extra-bold text-muted">
                                {getCurrencySymbol(currencyCode)}
                                {taxAmount.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                          {activeSelectedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{
                                borderTop: "1px solid #dee2e6",
                                fontSize: "1.05rem",
                              }}
                            >
                              <td></td>
                              <td className="text-start fw-bold">
                                {t("total")}
                              </td>
                              <td className="mulish-extra-bold">
                                {getCurrencySymbol(currencyCode)}
                                {totalWithTax.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer bg-gray d-flex justify-content-center align-items-center gap-2"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignedPage;
